import React, { useState, useContext, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import config from '../../config/config';
import LoggingService from "../../services/LoggingService";

import "../../styles/Auth.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { loginContext } = useContext(AuthContext);
  const navigate = useNavigate();

  const checkAuthorizationStatus = useCallback(async () => {
    const token = localStorage.getItem("userToken");

    LoggingService.logAuth("Authorization Check", "Checking authorization status");

    if (token) {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/orchestrator/authorized`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // if (response.data.isAuthorized) {
        //   LoggingService.logAuth("Authorization Success", "User is authorized, navigating to dashboard");
        //   navigate("/dashboard");
        // } else {
        //   LoggingService.logAuth("Authorization Failed", "User is not authorized, navigating to premise-or-cloud");
        //   navigate("/premise-or-cloud");
        // }
        if (response.data.isAuthorized) {
          const env = localStorage.getItem("premiseOrCloud");

          if (!env) {
            LoggingService.logAuth("Authorization is needed.", "No environment selected.");
            navigate("/premise-or-cloud"); // Ortam bilgisi yoksa seçtir
          } else {
            LoggingService.logAuth("Authorization Success", "User is authorized, navigating to dashboard");
            navigate("/dashboard"); // Ortam bilgisi varsa normal yönlendir
          }

        } else {
          LoggingService.logAuth("Authorization Failed", "User is not authorized, navigating to premise-or-cloud");
          navigate("/premise-or-cloud"); // Yetkili değilse seçtir

        }
      } catch (error) {
        LoggingService.logError("Authorization Check Failed", `Error: ${error.message}`);
        if (error.response?.status === 401) {
          localStorage.removeItem("userToken");
          navigate("/login");
        } else {
          navigate("/premise-or-cloud");
        }
      }
    } else {
      LoggingService.logAuth("No Token", "No token found in localStorage");
      navigate("/login");
    }
  }, [navigate]);

  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (!isRedirecting && window.location.pathname !== "/login") {
      setIsRedirecting(true);
      navigate("/login");
    } else if (!isRedirecting) {
      checkAuthorizationStatus();
    }
  }, [checkAuthorizationStatus, navigate, isRedirecting]);

  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lastLoginAttempt, setLastLoginAttempt] = useState(null);
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_TIME = 1 * 60 * 1000; // 15 minutes in milliseconds
  const [remainingLockoutTime, setRemainingLockoutTime] = useState(0);

  useEffect(() => {
    let timer;
    if (remainingLockoutTime > 0) {
      timer = setInterval(() => {
        setRemainingLockoutTime(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0) {
            // Clear the error message when countdown reaches zero
            setError("");
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [remainingLockoutTime]);

  const handleLogin = async () => {
    setError("");

    // Check rate limiting
    const now = Date.now();
    if (loginAttempts >= MAX_ATTEMPTS && lastLoginAttempt && (now - lastLoginAttempt) < LOCKOUT_TIME) {
      const remainingTime = Math.ceil((LOCKOUT_TIME - (now - lastLoginAttempt)) / 1000);
      setRemainingLockoutTime(remainingTime);
      LoggingService.logAuth("Login Rate Limited", "Too many login attempts");
      setError(`Too many login attempts. Please try again in ${Math.ceil(remainingTime / 60)} minutes `);
      return;
    }

    // Reset attempts if lockout period has passed
    if (lastLoginAttempt && (now - lastLoginAttempt) >= LOCKOUT_TIME) {
      setLoginAttempts(0);
      setRemainingLockoutTime(0);
    }

    if (!email || !password) {
      LoggingService.logAuth("Login Validation Failed", "Missing email or password");
      setError("Please fill in both email and password.");
      return;
    }

    // Add email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      LoggingService.logAuth("Login Validation Failed", "Invalid email format");
      setError("Please enter a valid email address.");
      return;
    }

    try {
      LoggingService.logAuth("Login Attempt", `Login attempt for user: ${email}`);

      const response = await axios.post(`${config.API_BASE_URL}/api/auth/login`, { email, password });

      if (response.data.token && response.data.userId) {
        const { token, username, userId } = response.data;

        LoggingService.logAuth("Login Success", `User ${email} logged in successfully`);

        localStorage.setItem("userToken", token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userName", username);
        localStorage.setItem("userId", userId.toString());

        LoggingService.logSystem("User Data", `User data stored for ${email}`);

        await loginContext(email, password);
        checkAuthorizationStatus();
      } else {
        LoggingService.logAuth("Login Failed", "Invalid response from server");
        setError("Invalid login credentials.");
        setLoginAttempts(prev => prev + 1);
        setLastLoginAttempt(Date.now());
      }
    } catch (e) {
      LoggingService.logError("Login Error", `Login failed for ${email}`);

      // Increment login attempts and update last attempt timestamp for any failure
      setLoginAttempts(prev => prev + 1);
      setLastLoginAttempt(Date.now());

      if (e.response) {
        switch (e.response.status) {
          case 401:
            if (e.response.data.message === "No user found") {
              LoggingService.logAuth("Login Failed", `Email not found: ${email}`);
              setError("Email not found. Please check your email or sign up.");
            } else {
              LoggingService.logAuth("Login Failed", `Incorrect password for user: ${email}`);
              setError("Incorrect password. Please try again.");
            }
            break;
          case 404:
            LoggingService.logAuth("Login Failed", `Email not found: ${email}`);
            setError("Email not found. Please check your email or sign up.");
            break;
          case 500:
            LoggingService.logError("Server Error", "Internal server error during login");
            setError("Server error. Please try again later.");
            break;
          default:
            LoggingService.logError("Unknown Error", `Unexpected error during login: ${e.message}`);
            setError("An error occurred during login. Please try again later.");
        }
      } else {
        LoggingService.logError("Connection Error", "Unable to connect to server");
        setError("Unable to connect to the server. Please try again later.");
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="login-container">
      <Helmet>
        <title>Login - Autonomie</title>
      </Helmet>
      <div className="login-box">
        <h2>Login</h2>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Email"
          className="login-input"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Password"
          className="login-input"
        />

        <button
          onClick={handleLogin}
          className="login-button"
          disabled={remainingLockoutTime > 0}
          style={{
            opacity: remainingLockoutTime > 0 ? 0.5 : 1,
            cursor: remainingLockoutTime > 0 ? 'not-allowed' : 'pointer'
          }}
        >
          Login
        </button>
        {error && <p className="login-error">{error}</p>}
        {remainingLockoutTime > 0 && (
          <p className="login-error">
            Time remaining: {Math.floor(remainingLockoutTime / 60)}:{String(remainingLockoutTime % 60).padStart(2, '0')}
          </p>
        )}

      </div>
    </div>
  );
};

export default Login;
